import React from 'react';
import { User, Hash, Calendar, MapPin, Mail, Phone, Globe, Users, BookOpen, GraduationCap, Award, Info } from 'lucide-react';

const StudentInfo = ({ etudiant, classe, bulletin }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const getInscriptionInfo = () => {
    if (bulletin?.informations_inscription) {
      return bulletin.informations_inscription;
    }
    return null;
  };

  const inscriptionInfo = getInscriptionInfo();

  return (
    <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 rounded-2xl p-4 sm:p-6 lg:p-8 border border-blue-200 shadow-lg">
      {/* En-tête avec icône et titre */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 mb-6 lg:mb-8">
        <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
          <User className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 break-words">Informations de l'étudiant</h3>
          <p className="text-blue-600 font-medium text-sm sm:text-base">Données personnelles et académiques</p>
        </div>
      </div>
      
      {/* Informations personnelles */}
      <div className="bg-white rounded-xl p-4 sm:p-6 mb-6 border border-blue-100 shadow-sm">
        <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <Award className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-blue-600 flex-shrink-0" />
          <span className="break-words">Données personnelles</span>
        </h4>
        
        <div className="grid grid-cols-1 gap-3 sm:gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
            <span className="text-gray-700 font-medium flex items-center mb-2 sm:mb-0">
              <User className="h-4 w-4 mr-2 text-blue-600 flex-shrink-0" />
              <span className="break-words">Nom et Prénoms:</span>
            </span>
            <span className="font-semibold text-gray-900 text-sm sm:text-base break-words text-right sm:text-left">
              {etudiant ? `${etudiant.nom} ${etudiant.prenom}` : 'N/A'}
            </span>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
            <span className="text-gray-700 font-medium flex items-center mb-2 sm:mb-0">
              <Hash className="h-4 w-4 mr-2 text-blue-600 flex-shrink-0" />
              <span className="break-words">Matricule:</span>
            </span>
            <span className="font-semibold text-gray-900 text-sm sm:text-base break-words text-right sm:text-left">
              {etudiant?.matricule || 'Non attribué'}
            </span>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
            <span className="text-gray-700 font-medium flex items-center mb-2 sm:mb-0">
              <Calendar className="h-4 w-4 mr-2 text-blue-600 flex-shrink-0" />
              <span className="break-words">Date de naissance:</span>
            </span>
            <span className="font-semibold text-gray-900 text-sm sm:text-base break-words text-right sm:text-left">
              {etudiant?.date_naissance ? formatDate(etudiant.date_naissance) : 'Non renseignée'}
            </span>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
            <span className="text-gray-700 font-medium flex items-center mb-2 sm:mb-0">
              <MapPin className="h-4 w-4 mr-2 text-blue-600 flex-shrink-0" />
              <span className="break-words">Lieu de naissance:</span>
            </span>
            <span className="font-semibold text-gray-900 text-sm sm:text-base break-words text-right sm:text-left">
              {etudiant?.lieu_naissance || 'Non renseigné'}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
            <span className="text-gray-700 font-medium flex items-center mb-2 sm:mb-0">
              <Globe className="h-4 w-4 mr-2 text-blue-600 flex-shrink-0" />
              <span className="break-words">Nationalité:</span>
            </span>
            <span className="font-semibold text-gray-900 text-sm sm:text-base break-words text-right sm:text-left">
              {etudiant?.nationalite || 'Non renseignée'}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
            <span className="text-gray-700 font-medium flex items-center mb-2 sm:mb-0">
              <User className="h-4 w-4 mr-2 text-blue-600 flex-shrink-0" />
              <span className="break-words">Genre:</span>
            </span>
            <span className="font-semibold text-gray-900 text-sm sm:text-base break-words text-right sm:text-left">
              {etudiant?.genre || 'Non renseigné'}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
            <span className="text-gray-700 font-medium flex items-center mb-2 sm:mb-0">
              <Phone className="h-4 w-4 mr-2 text-blue-600 flex-shrink-0" />
              <span className="break-words">Contact:</span>
            </span>
            <span className="font-semibold text-gray-900 text-sm sm:text-base break-words text-right sm:text-left">
              {etudiant?.contact || 'Non renseigné'}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
            <span className="text-gray-700 font-medium flex items-center mb-2 sm:mb-0">
              <MapPin className="h-4 w-4 mr-2 text-blue-600 flex-shrink-0" />
              <span className="break-words">Adresse:</span>
            </span>
            <span className="font-semibold text-gray-900 text-sm sm:text-base break-words text-right sm:text-left">
              {etudiant?.adresse || 'Non renseignée'}
            </span>
          </div>

          {etudiant?.email && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
              <span className="text-gray-700 font-medium flex items-center mb-2 sm:mb-0">
                <Mail className="h-4 w-4 mr-2 text-blue-600 flex-shrink-0" />
                <span className="break-words">Email:</span>
              </span>
              <span className="font-semibold text-gray-900 text-sm sm:text-base break-words text-right sm:text-left">
                {etudiant.email}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Informations académiques */}
      <div className="bg-white rounded-xl p-4 sm:p-6 border border-blue-100 shadow-sm">
        <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <GraduationCap className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-blue-600 flex-shrink-0" />
          <span className="break-words">Informations académiques</span>
        </h4>
        
        <div className="grid grid-cols-1 gap-3 sm:gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
            <span className="text-gray-700 font-medium flex items-center mb-2 sm:mb-0">
              <BookOpen className="h-4 w-4 mr-2 text-blue-600 flex-shrink-0" />
              <span className="break-words">Filière:</span>
            </span>
            <span className="font-semibold text-gray-900 text-sm sm:text-base break-words text-right sm:text-left">
              {inscriptionInfo?.filiere || 'Non définie'}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
            <span className="text-gray-700 font-medium flex items-center mb-2 sm:mb-0">
              <Users className="h-4 w-4 mr-2 text-blue-600 flex-shrink-0" />
              <span className="break-words">Niveau:</span>
            </span>
            <span className="font-semibold text-gray-900 text-sm sm:text-base break-words text-right sm:text-left">
              {classe?.niveau?.libelle || inscriptionInfo?.niveau || 'Non défini'}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
            <span className="text-gray-700 font-medium flex items-center mb-2 sm:mb-0">
              <Calendar className="h-4 w-4 mr-2 text-blue-600 flex-shrink-0" />
              <span className="break-words">Date d'inscription:</span>
            </span>
            <span className="font-semibold text-gray-900 text-sm sm:text-base break-words text-right sm:text-left">
              {inscriptionInfo?.date_inscription ? formatDate(inscriptionInfo.date_inscription) : 'Non renseignée'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentInfo;
